/**
 * The days a UTC range touches (`UtcDays.touched`): the one conversion from
 * an instant range to the day range a cell read names, owned here, in the
 * rows-side core, so every layer that reads cells by UTC day asks the same
 * rule: a keyed row read (`RowFetchPlan`), the adapter's row read
 * (`SdkBackendAdapter.fetchRowCells`), the accessor's sheet-wide read
 * (`CellAccessor`), a row's events load (`RowEvents.ensureLoaded`), the sync
 * backend's range read, the mutation substrate's snapshot
 * (`CloudMutationBackend.snapshot`), a store change's days
 * (`CellStoreView`) and the DOM's sheet-wide loads (`renderedLoadDays`,
 * through `@tento-lona/sheets/internal-ui`). The SDK layer takes it from
 * `@tento-lona/sheets/internal-sdk`.
 *
 * @module
 */
import { type DateTime, NaiveDate, type Utc } from "@tento-chrono";

/** See the module note. */
export namespace UtcDays {
  const DAY_MS = 86_400_000;

  /**
   * The half-open day range `range` touches: from its start's day to its
   * end's day, plus that day when the end is past its midnight (09:00-17:00
   * on D reads D..D+1, never the empty D..D; an end at midnight closes on its
   * own date). An empty range, an instant, touches the day it falls in, a
   * midnight instant included (the day-membership convention:
   * `TimeMembership.daysOf` of a UTC range, which this rule never disagrees
   * with). On a midnight-aligned range — every range the accessor's
   * sheet-wide read is given — it is the plain day truncation.
   */
  export function touched(range: DateTime.Range<Utc>): NaiveDate.Range {
    const end = range.end.mse;
    const lastDay = end > range.start.mse && end % DAY_MS === 0 ? range.end.date : range.end.date.addDays(1);
    return new NaiveDate.Range(range.start.date, lastDay);
  }
}
